import type { AppRoute } from 'src/routes/types';

import { AppBar, IconButton, Stack, Toolbar } from '@mui/material';
// @mui
import { useTheme } from '@mui/material/styles';

import Iconify from '../../../components/iconify';
// components
import Logo from '../../../components/logo';
import { useSettingsContext } from '../../../components/settings';
// config
import { HEADER, NAV } from '../../../config-global';
// hooks
import useOffSetTop from '../../../hooks/useOffSetTop';
import useResponsive from '../../../hooks/useResponsive';
// utils
import { bgBlur } from '../../../utils/cssStyles';
import AccountPopover from './AccountPopover';
import LanguagePopover from './LanguagePopover';
//
import Searchbar from './Searchbar';

// ----------------------------------------------------------------------

type Props = {
  onOpenNav?: VoidFunction;
  privateAppRoutes: AppRoute[];
};

const Header = ({ onOpenNav, privateAppRoutes }: Props) => {
  const theme = useTheme();

  const { themeLayout } = useSettingsContext();

  const isNavHorizontal = themeLayout === 'horizontal';

  const isNavMini = themeLayout === 'mini';

  const isDesktop = useResponsive('up', 'lg');

  const isOffset = useOffSetTop(HEADER.H_DASHBOARD_DESKTOP) && !isNavHorizontal;

  const renderContent = (
    <>
      {isDesktop && isNavHorizontal && (
        <Logo sx={{ justifyContent: 'flex-start', mr: 2.5, mt: -1 }} />
      )}

      {!isDesktop && (
        <IconButton onClick={onOpenNav} sx={{ color: 'text.primary', mr: 1 }}>
          <Iconify icon="eva:menu-2-fill" />
        </IconButton>
      )}

      <Searchbar privateAppRoutes={privateAppRoutes} />

      <Stack
        alignItems="center"
        direction="row"
        flexGrow={1}
        justifyContent="flex-end"
        spacing={{ sm: 3, xs: 2 }}
      >
        <LanguagePopover />

        <AccountPopover />
      </Stack>
    </>
  );

  return (
    <AppBar
      sx={{
        boxShadow: 'none',
        height: HEADER.H_MOBILE,
        zIndex: theme.zIndex.appBar + 1,
        ...bgBlur({
          color: theme.palette.background.default,
        }),
        transition: theme.transitions.create(['height'], {
          duration: theme.transitions.duration.shorter,
        }),
        ...(isDesktop && {
          height: HEADER.H_DASHBOARD_DESKTOP,
          width: `calc(100% - ${NAV.W_DASHBOARD + 1}px)`,
          ...(isOffset && {
            height: HEADER.H_DASHBOARD_DESKTOP_OFFSET,
          }),
          ...(isNavHorizontal && {
            bgcolor: 'background.default',
            borderBottom: `dashed 1px ${theme.palette.divider}`,
            height: HEADER.H_DASHBOARD_DESKTOP_OFFSET,
            width: 1,
          }),
          ...(isNavMini && {
            width: `calc(100% - ${NAV.W_DASHBOARD_MINI + 1}px)`,
          }),
        }),
      }}
    >
      <Toolbar
        sx={{
          height: 1,
          px: { lg: 5 },
        }}
      >
        {renderContent}
      </Toolbar>
    </AppBar>
  );
};

export default Header;
